import { useState } from 'react';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { TrendingUp, AlertTriangle, ArrowRight } from 'lucide-react';

const EnhancementDetails = ({ assessment, previousAssessment, onUpdateAssessment }) => {
  const prevLimit = assessment?.sanctioned_limit_previous || previousAssessment?.sanctioned_limit_approved || 0;
  const [requestedLimit, setRequestedLimit] = useState(assessment?.sanctioned_limit_requested || '');
  const [rationale, setRationale] = useState(assessment?.enhancement_rationale || '');

  const increasePct = prevLimit > 0 && requestedLimit > 0
    ? (((requestedLimit - prevLimit) / prevLimit) * 100).toFixed(1)
    : 0;

  const handleSave = () => {
    onUpdateAssessment({
      sanctioned_limit_requested: Number(requestedLimit),
      enhancement_rationale: rationale,
    });
  };

  return (
    <div className="space-y-5">
      <div>
        <h3 className="text-lg font-semibold text-gray-900 mb-1" data-testid="enhancement-details-title">
          Enhancement Details
        </h3>
        <p className="text-sm text-gray-500">
          Specify the proposed new limit, rationale, and what has changed since the last cycle.
        </p>
      </div>

      {/* Previous Limit Card */}
      <Card className="bg-gray-50">
        <CardContent className="pt-4">
          <div className="text-xs text-gray-500 uppercase tracking-wide mb-1">Previous Sanctioned Limit</div>
          <div className="text-2xl font-bold text-gray-900">
            Rs. {Number(prevLimit).toLocaleString()}
          </div>
          <div className="text-xs text-gray-400 mt-1">
            Cycle {previousAssessment?.cycle_number || '1'} &mdash; {previousAssessment?.scoring_date?.slice(0, 10) || 'N/A'}
          </div>
        </CardContent>
      </Card>

      {/* Requested Limit */}
      <div className="space-y-2">
        <Label className="text-sm font-medium">
          Requested New Limit <span className="text-red-500">*</span>
        </Label>
        <div className="relative">
          <span className="absolute left-3 top-2.5 text-sm text-gray-500">Rs.</span>
          <Input
            type="number"
            value={requestedLimit}
            onChange={e => setRequestedLimit(e.target.value)}
            className="pl-10"
            placeholder="Enter proposed limit"
            data-testid="requested-limit-input"
          />
        </div>
        {Number(requestedLimit) > 0 && (
          <div className="flex items-center gap-2">
            <Badge className={`text-xs ${Number(increasePct) > 50 ? 'bg-red-100 text-red-800' : 'bg-blue-100 text-blue-800'}`}>
              <TrendingUp className="h-3 w-3 mr-1" />
              {increasePct}% increase
            </Badge>
            {Number(increasePct) > 50 && (
              <span className="text-xs text-red-600 flex items-center gap-1">
                <AlertTriangle className="h-3 w-3" /> Exceeds typical policy ceiling (50%)
              </span>
            )}
          </div>
        )}
      </div>

      {/* Rationale */}
      <div className="space-y-2">
        <Label className="text-sm font-medium">
          Enhancement Rationale <span className="text-red-500">*</span>
        </Label>
        <Textarea
          value={rationale}
          onChange={e => setRationale(e.target.value)}
          placeholder="Explain why the limit should be enhanced: expansion in cultivated area, new crop, higher yield, better repayment track..."
          rows={4}
          data-testid="enhancement-rationale-input"
        />
      </div>

      {/* Side-by-side comparison placeholder */}
      <Card className="border-dashed border-gray-300">
        <CardContent className="pt-4">
          <div className="text-xs text-gray-400 uppercase tracking-wide mb-2">
            Side-by-Side Comparison (auto-populated from changed fields)
          </div>
          {(assessment?.changed_fields || []).length > 0 ? (
            <div className="space-y-1.5">
              {assessment.changed_fields.slice(0, 8).map((cf, i) => (
                <div key={i} className="grid grid-cols-3 gap-2 text-xs py-1 border-b border-gray-100">
                  <span className="text-gray-600 font-medium">{cf.field}</span>
                  <span className="text-gray-400 text-right">{String(cf.previous_value ?? 'N/A').slice(0, 20)}</span>
                  <span className="text-amber-700 font-medium flex items-center gap-1">
                    <ArrowRight className="h-3 w-3 text-gray-300" />
                    {String(cf.current_value ?? 'N/A').slice(0, 20)}
                  </span>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-xs text-gray-400">No changes detected between current profile and previous cycle snapshot.</div>
          )}
        </CardContent>
      </Card>

      <Button onClick={handleSave} style={{ background: '#7c3aed' }} data-testid="save-enhancement-btn">
        Save Enhancement Details
      </Button>
    </div>
  );
};

export default EnhancementDetails;
